import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGuestGate } from "../lib/useGuestGate";
import { summarizeTrades, money } from "../lib/history";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

export default function HistoryPage() {
  const { gate } = useGuestGate();
  const [years, setYears] = useState([]);
  const [tradesByYear, setTradesByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newYear, setNewYear] = useState("");
  const [newCar, setNewCar] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/trade-years");
      setYears(data);
      // Fetch trades for each year in parallel to compute totals
      const tradePairs = await Promise.all(
        data.map((y) =>
          api
            .get(`/trade-years/${y.id}/trades`)
            .then((r) => [y.id, r.data])
            .catch(() => [y.id, []])
        )
      );
      setTradesByYear(Object.fromEntries(tradePairs));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddYear = async (e) => {
    e.preventDefault();
    if (!newYear) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/trade-years", {
        year: Number(newYear),
        car: Number(newCar || 0),
      });
      setYears((prev) => [...prev, data].sort((a, b) => a.year - b.year));
      setTradesByYear((prev) => ({ ...prev, [data.id]: [] }));
      toast.success(`Year ${data.year} added`);
      setOpen(false);
      setNewYear("");
      setNewCar("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add year");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, year) => {
    try {
      await api.delete(`/trade-years/${id}`);
      setYears((prev) => prev.filter((y) => y.id !== id));
      toast.success(`Year ${year} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove year");
    } finally {
      setConfirmTarget(null);
    }
  };

  const rows = useMemo(
    () =>
      years.map((y) => {
        const trades = tradesByYear[y.id] || [];
        const { totalPL, pct } = summarizeTrades(trades, y.car);
        return { ...y, totalPL, pct, count: trades.length };
      }),
    [years, tradesByYear]
  );

  return (
    <div className="space-y-8" data-testid="history-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <p className="text-slate-600 text-sm leading-relaxed max-w-xl">
          Yearly results across the desk. Click any year for the full trade log.
          <span className="block mt-1 text-slate-500">
            Our financial year runs from May 1 to May 1.
          </span>
        </p>
        <Dialog open={open} onOpenChange={(o) => { if (o && gate("Adding a year")) return; setOpen(o); }}>
          <DialogTrigger asChild>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
              data-testid="add-year-button"
            >
              <Plus className="w-4 h-4" />
              Add Year
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="add-year-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Add Year</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddYear} className="space-y-4">
              <div>
                <Label
                  htmlFor="new-year"
                  className="text-xs tracking-[0.15em] uppercase text-slate-600"
                >
                  Year Number
                </Label>
                <Input
                  id="new-year"
                  type="number"
                  inputMode="numeric"
                  step={1}
                  min={1}
                  required
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  placeholder="1"
                  className="mt-1.5 font-mono tabular-nums"
                  data-testid="new-year-input"
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <Label
                    htmlFor="new-car"
                    className="text-xs tracking-[0.15em] uppercase text-slate-600"
                  >
                    Capital At Risk per Option
                  </Label>
                  <span className="text-xs text-slate-400">$</span>
                </div>
                <Input
                  id="new-car"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  value={newCar}
                  onChange={(e) => setNewCar(e.target.value)}
                  placeholder="0.00"
                  className="mt-1.5 font-mono tabular-nums"
                  data-testid="new-car-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="cancel-year-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="submit-year-button"
                >
                  {submitting ? "Adding..." : "Add Year"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="years-table"
      >
        <table className="w-full min-w-[720px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Year</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">CAR per option</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Trades</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">P/L per option</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">% of CAR</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-slate-500">
                  Loading history...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center" data-testid="empty-history">
                  <div className="text-sm text-slate-500">No years yet.</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Click "Add Year" to record your first trading year.
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((y) => {
                const positive = y.totalPL >= 0;
                return (
                  <tr
                    key={y.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`year-row-${y.year}`}
                  >
                    <td className="py-4 px-6">
                      <Link
                        to={`/history/${y.id}`}
                        className="inline-flex items-center gap-2 font-mono font-semibold text-slate-900 text-base tracking-wide hover:text-slate-600 transition-colors"
                        data-testid={`open-year-${y.year}`}
                      >
                        Year {y.year}
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </Link>
                    </td>
                    <td className="py-4 px-6 text-right font-mono tabular-nums text-sm text-slate-700">
                      {money(y.car)}
                    </td>
                    <td className="py-4 px-6 text-right font-mono tabular-nums text-sm text-slate-700">
                      {y.count}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div
                        className={`inline-flex items-center gap-1.5 font-mono tabular-nums text-sm font-semibold ${
                          positive ? "text-emerald-700" : "text-red-600"
                        }`}
                      >
                        {positive ? (
                          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.25} />
                        ) : (
                          <ArrowDownRight className="w-3.5 h-3.5" strokeWidth={2.25} />
                        )}
                        {money(y.totalPL)}
                      </div>
                    </td>
                    <td
                      className={`py-4 px-6 text-right font-mono tabular-nums text-sm font-semibold ${
                        y.pct === null
                          ? "text-slate-400"
                          : y.pct >= 0
                          ? "text-emerald-700"
                          : "text-red-600"
                      }`}
                    >
                      {y.pct === null
                        ? "—"
                        : `${y.pct >= 0 ? "+" : ""}${y.pct.toFixed(2)}%`}
                    </td>
                    <td className="py-4 px-6 text-right w-[120px]">
                      <button
                        type="button"
                        onClick={() => { if (gate("Removing a year")) return; setConfirmTarget({ id: y.id, year: y.year }); }}
                        aria-label={`Remove year ${y.year}`}
                        title={`Remove year ${y.year}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        data-testid={`delete-year-${y.year}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="delete-year-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete year?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove Year {confirmTarget?.year} and all of its trades.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-year">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmTarget && handleDelete(confirmTarget.id, confirmTarget.year)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-year"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
